import { useMemo } from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { ButtonProps } from 'shared/mui/Buttons';
import { flatObject } from 'utils/flatObject';

import { FilterResetButtonStyled } from './FilterResetButton.styled';

export type FilterResetButtonProps = ButtonProps & {
  onReset?: () => void;
};

export const FilterResetButton: React.FC<FilterResetButtonProps> = ({
  color = 'primary',
  variant = 'outlined',
  label,
  onReset,
  disabled,
  ...restProps
}) => {
  const translate = useTypedTranslate();
  const {
    formState: { isDirty, dirtyFields, defaultValues },
    reset,
  } = useFormContext();
  const { filterValues = {} } = useListContext();

  const hasDirtyFields = useMemo<boolean>(() => {
    if (typeof defaultValues !== 'object') {
      return false;
    }

    return Object.values(defaultValues).some((value) => {
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
      if (typeof value === 'object' && value) {
        return Object.values(flatObject(value)).every((val) => Boolean(val));
      }
      return Boolean(value);
    });
  }, [defaultValues]);

  const isEmptyUrlFilters = Object.keys(filterValues).length === 0;
  const computedDisabled =
    isEmptyUrlFilters && (!(isDirty || hasDirtyFields) || Object.keys(dirtyFields).length === 0);

  function onClick() {
    reset();
    if (onReset) {
      onReset();
    }
  }

  return (
    <FilterResetButtonStyled
      color={color}
      disabled={disabled ?? computedDisabled}
      label={label || translate('app.buttons.reset')}
      onClick={onClick}
      variant={variant}
      {...restProps}
    />
  );
};
